import { useMutation } from '@apollo/client';
import { useContext, useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import Button from '../../../components/Button/Button';
import Loading from '../../../components/Loading';
import miniImage from '../../../assets/images/fi-mini.png';
import { trackNanoRescueCollarAnswered, trackNanoRescueCollarQuestionViewed } from '@/lib/analytics/events';
import {
  AdopterClaimRescueCollarData,
  AdopterRescueCollarVariables,
  AdopterReturnRescueCollarData,
  adopterClaimRescueCollarMutation,
  adopterReturnRescueCollarMutation,
} from '../RegisterChip.graphql';
import RegisterChipContext from '../RegisterChipContextV2';
import { useRescueCollarStatus } from '../useRescueCollarStatus';
import { useScrollToStepTop } from '../useScrollToStepTop';
import styles from './RescueCollarStep.module.scss';

type Outcome = 'alreadyResolved' | 'failed';

/**
 * Asks an adopter whether their rescue sent them home with the Fi Mini on their pet, when Nano
 * registration left a rescue's collar on it to ask about. Yes opens a pending adoption transfer for
 * the collar and goes straight to the confirmation, which leaves out the GPS offer: the adopter
 * already has the tracker it sells. No returns the collar to the rescue and carries on like any
 * registration without one, through the upgrade step where it is still walked.
 *
 * A pet with no such collar goes straight on to the upgrade step, so this screen only appears when
 * there is something to ask.
 */
export default function RescueCollarStep() {
  const history = useHistory();
  const { onStepFinished, setRescueCollarClaimed } = useContext(RegisterChipContext);
  const { loading, petId, hasUnresolvedRescueCollar, refetch } = useRescueCollarStatus();
  const [claimCollar] = useMutation<AdopterClaimRescueCollarData, AdopterRescueCollarVariables>(
    adopterClaimRescueCollarMutation,
  );
  const [returnCollar] = useMutation<AdopterReturnRescueCollarData, AdopterRescueCollarVariables>(
    adopterReturnRescueCollarMutation,
  );
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const asking = !loading && !!petId && hasUnresolvedRescueCollar;

  useScrollToStepTop(!loading);

  const trackedView = useRef(false);
  useEffect(() => {
    if (asking && petId && !trackedView.current) {
      trackedView.current = true;
      trackNanoRescueCollarQuestionViewed({ petId });
    }
  }, [asking, petId]);

  const answer = async (hasCollar: boolean) => {
    if (!petId || submitting) {
      return;
    }
    setSubmitting(true);
    setOutcome(null);
    try {
      const variables = { input: { petId } };
      if (hasCollar) {
        await claimCollar({ variables });
        setRescueCollarClaimed(true);
        trackNanoRescueCollarAnswered({ petId, hasCollar });
        history.push('/register/success');
      } else {
        await returnCollar({ variables });
        trackNanoRescueCollarAnswered({ petId, hasCollar });
        onStepFinished('rescueCollar');
      }
    } catch {
      // The backend reports Conflict and NotFound only as translated messages, so ask again
      // instead: if the collar no longer needs an answer, something already resolved it. That may
      // be this answer, if only its response was lost, so the message doesn't say who.
      const { data } = await refetch().catch(() => ({ data: undefined }));
      const resolved = data?.pet?.hasUnresolvedRescueCollar === false;
      // A Yes that left the collar resolved most likely went through with only its response lost,
      // so the confirmation should leave out the GPS offer as it would have
      if (resolved && hasCollar) {
        setRescueCollarClaimed(true);
      }
      setOutcome(resolved ? 'alreadyResolved' : 'failed');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  // While an answer is in flight the status refetch can clear the collar before the outcome is
  // set, which must show the outcome rather than skip ahead.
  if (!asking && !submitting && outcome === null) {
    return <Redirect to="/register/upgrade" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <img className={styles.miniImage} alt="Fi Mini" src={miniImage} />
        <div className={styles.titleContainer}>
          <div className={styles.title}>Did your rescue send you home with a Fi Mini?</div>
          <div className={styles.subtitle}>
            Some rescues and shelters send their dogs home wearing a Fi Mini GPS tracker on the collar.
          </div>
        </div>

        {outcome === 'alreadyResolved' ? (
          <div className={styles.actions}>
            <div className={styles.message}>
              This collar has already been taken care of, so there&apos;s nothing more to do here.
            </div>
            <Button primary label="Continue" onClick={() => history.push('/register/success')} />
          </div>
        ) : (
          <div className={styles.actions}>
            {outcome === 'failed' && (
              <div className={styles.error}>Something went wrong saving your answer. Please try again.</div>
            )}
            <Button primary label="Yes, I have it" disabled={submitting} onClick={() => answer(true)} />
            <Button label="No" disabled={submitting} onClick={() => answer(false)} />
            {/* Some refusals repeat on every retry (no deferral for this user, a rescue with no
                owner), so a failed answer never blocks registration. The sweep returns an
                unanswered collar to the rescue. */}
            {outcome === 'failed' && (
              <button
                type="button"
                className={styles.skip}
                disabled={submitting}
                onClick={() => history.push('/register/success')}
              >
                Continue without answering
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
